/**
 * headlight-dashboard-access — which dashboards a user may open, the one they land on, and the data filter their roles
 * bind to each.
 *
 * Rules are data: a map in the `DashboardAccess` configuration namespace (NamespaceValue `Rules`). A rule grants the
 * dashboards it lists to an audience of roles. Customer roles grant every listed dashboard; project roles grant only the
 * project-bound ones, and only for the project being viewed.
 *
 * DOM-free so the resolver endpoint and the config app's preview answer identically.
 */

const libNavigation = require('headlight-entity-navigation');

const NAMESPACE = 'DashboardAccess';
const NAMESPACE_VALUE = 'Rules';

/**
 * Filter operators a rule may bind, with their meadow filter spelling and plain-language label.
 *
 * @type {Record<string, { Meadow: string, List: boolean, Label: string }>}
 */
const OPERATORS =
{
	EQ: { Meadow: 'EQ', List: false, Label: 'is' },
	NE: { Meadow: 'NE', List: false, Label: 'is not' },
	GT: { Meadow: 'GT', List: false, Label: 'is more than' },
	GE: { Meadow: 'GE', List: false, Label: 'is at least' },
	LT: { Meadow: 'LT', List: false, Label: 'is less than' },
	LE: { Meadow: 'LE', List: false, Label: 'is at most' },
	LK: { Meadow: 'LK', List: false, Label: 'matches' },
	IN: { Meadow: 'INN', List: true, Label: 'is one of' },
	NI: { Meadow: 'NIN', List: true, Label: 'is not one of' },
};

/**
 * Values a filter can take from the person viewing, with the phrase the config screen shows for each. `Missing` is what
 * the person is told when they have no such value, so the filter is dropped and they see more than usual: what is
 * missing, what they see instead, and who can set it up.
 *
 * @type {Record<string, { Label: string, List: boolean, Missing?: { Title: string, Message: string } }>}
 */
const TOKENS =
{
	'User.IDUser': { Label: 'the person viewing', List: false },
	'User.IDCustomer': { Label: 'their organization', List: false },
	'User.LabIDs':
	{
		Label: 'their labs', List: true,
		Missing:
		{
			Title: 'Showing every lab, not just yours',
			Message: 'You aren\'t on a lab team in HeadLight, so this dashboard can\'t tell which lab is yours. To see only your lab\'s work, ask your lab supervisor or HeadLight administrator to add you to your lab\'s team.',
		},
	},
	'User.TeamIDs':
	{
		Label: 'their teams', List: true,
		Missing:
		{
			Title: 'Showing every team, not just yours',
			Message: 'You aren\'t on a team in HeadLight, so this dashboard can\'t narrow itself to your team\'s work. Ask your supervisor or HeadLight administrator to add you to your team.',
		},
	},
	'User.TeamHashes':
	{
		Label: 'their teams (by team code)', List: true,
		Missing:
		{
			Title: 'Showing every team, not just yours',
			Message: 'You aren\'t on a team in HeadLight, so this dashboard can\'t narrow itself to your team\'s work. Ask your supervisor or HeadLight administrator to add you to your team.',
		},
	},
	'User.ProjectIDs':
	{
		Label: 'their projects', List: true,
		Missing:
		{
			Title: 'Showing every project, not just yours',
			Message: 'You aren\'t a member of any project in HeadLight, so this dashboard can\'t narrow itself to your projects. Ask the project engineer or your HeadLight administrator to add you to your projects.',
		},
	},
	'User.CertifiedTestHashes':
	{
		Label: 'the tests they are certified for', List: true,
		Missing:
		{
			Title: 'Showing every test, not just the ones you\'re certified for',
			Message: 'HeadLight doesn\'t have your test certifications yet, so this dashboard can\'t narrow itself to the tests you\'re certified for.',
		},
	},
	'Context.IDProject': { Label: 'the project being viewed', List: false },
};

const TOKEN_PATTERN = /\{~D:([A-Za-z0-9_.]+)~\}/g;
const WHOLE_TOKEN_PATTERN = /^\{~D:([A-Za-z0-9_.]+)~\}$/;

/**
 * @typedef {Object} FilterClause
 * @property {string} Field - Column on the dashboard's data source.
 * @property {string} Operator - A key of OPERATORS.
 * @property {any} Value - A literal, a list, or a `{~D:User.LabIDs~}` style token.
 */

/**
 * @typedef {Object} AccessRule
 * @property {string} [Label] - Plain-language name, e.g. "Lab technicians".
 * @property {number} [Order] - Lower runs first. Default 100.
 * @property {boolean} [Enabled] - false turns the rule off.
 * @property {{ Roles?: Array<string>, PermissionLevels?: Array<string> }} Audience - GUIDRole (or role name) values.
 * @property {Array<string>} Dashboards - Dashboard hashes this rule grants.
 * @property {string} [Landing] - The dashboard to open first. When the customer doesn't have it (yet), the first dashboard
 *   this rule does grant, unless another rule's own landing applies.
 * @property {Record<string, Array<FilterClause>>} [DataFilters] - Per dashboard hash.
 */

/**
 * @typedef {Object} RoleRecord
 * @property {string} [Hash] - GUIDRole.
 * @property {string} [Name]
 * @property {string} [PermissionLevel]
 */

/**
 * @typedef {Object} CatalogEntry
 * @property {string} Hash
 * @property {boolean} [ProjectBound]
 */

/**
 * @typedef {Object} ResolvedDashboard
 * @property {string} Hash
 * @property {Array<string>} GrantedBy - Rule keys (or 'super-user' / 'no rules').
 * @property {Array<FilterClause>} DataFilter - Clauses with their values resolved.
 * @property {string} DataFilterString - The same clauses as a meadow filter string ('' when none).
 * @property {Array<FilterClause>} Unresolved - Clauses dropped because a token had no value for this user.
 */

/**
 * @param {any} pObject
 * @param {string} pAddress - Dotted path.
 * @return {any}
 */
function getAtAddress(pObject, pAddress)
{
	let tmpValue = pObject;
	for (const tmpPart of String(pAddress).split('.'))
	{
		if (tmpValue === null || tmpValue === undefined)
		{
			return undefined;
		}
		tmpValue = tmpValue[tmpPart];
	}
	return tmpValue;
}

/**
 * @param {Record<string, AccessRule>} pRules
 * @return {Array<[string, AccessRule]>} enabled rules, by Order then key
 */
function orderRules(pRules)
{
	return Object.entries(pRules || {})
		.filter(([ , pRule ]) => pRule && typeof pRule === 'object' && pRule.Enabled !== false && Array.isArray(pRule.Dashboards))
		.sort(([ pKeyA, pA ], [ pKeyB, pB ]) => ((pA.Order ?? 100) - (pB.Order ?? 100)) || pKeyA.localeCompare(pKeyB));
}

/**
 * @param {Record<string, AccessRule>} pRules
 * @return {boolean} true when at least one enabled rule exists — with none, access control is off for the customer
 */
function hasRules(pRules)
{
	return orderRules(pRules).length > 0;
}

/**
 * A rule with no roles and no permission levels matches nobody: a rule must say who it is for.
 *
 * @param {AccessRule['Audience']} pAudience
 * @param {Array<RoleRecord>} pRoles
 * @return {{ Match: boolean, Reason?: string }}
 */
function matchRoles(pAudience, pRoles)
{
	const tmpHasRoles = pAudience && Array.isArray(pAudience.Roles) && pAudience.Roles.length > 0;
	const tmpHasLevels = pAudience && Array.isArray(pAudience.PermissionLevels) && pAudience.PermissionLevels.length > 0;
	if (!tmpHasRoles && !tmpHasLevels)
	{
		return { Match: false, Reason: 'no audience' };
	}
	return libNavigation.matchAudience({ Roles: pAudience.Roles, PermissionLevels: pAudience.PermissionLevels }, { UserRoles: pRoles || [] }, null);
}

/**
 * Resolve one clause's value against `{ User, Context }`.
 *
 * @param {FilterClause} pClause
 * @param {Object} pData
 * @return {FilterClause|null} null when the clause can't be applied for this user
 */
function resolveClause(pClause, pData)
{
	const tmpOperator = OPERATORS[pClause && pClause.Operator];
	if (!tmpOperator || !pClause.Field)
	{
		return null;
	}
	let tmpValue = pClause.Value;
	if (typeof tmpValue === 'string')
	{
		const tmpWhole = WHOLE_TOKEN_PATTERN.exec(tmpValue);
		if (tmpWhole)
		{
			tmpValue = getAtAddress(pData, tmpWhole[1]);
		}
		else
		{
			let tmpMissing = false;
			tmpValue = tmpValue.replace(TOKEN_PATTERN, (pMatch, pAddress) =>
			{
				const tmpPart = getAtAddress(pData, pAddress);
				if (tmpPart === undefined || tmpPart === null || tmpPart === '' || Array.isArray(tmpPart))
				{
					tmpMissing = true;
					return '';
				}
				return String(tmpPart);
			});
			if (tmpMissing)
			{
				return null;
			}
		}
	}
	if (tmpOperator.List)
	{
		const tmpList = Array.isArray(tmpValue) ? tmpValue : (tmpValue === undefined || tmpValue === null || tmpValue === '') ? [] : String(tmpValue).split(',');
		const tmpClean = tmpList.map((pItem) => String(pItem).trim()).filter((pItem) => pItem !== '');
		if (tmpClean.length < 1)
		{
			return null;
		}
		return { Field: pClause.Field, Operator: pClause.Operator, Value: tmpClean };
	}
	if (tmpValue === undefined || tmpValue === null || tmpValue === '' || Array.isArray(tmpValue) || typeof tmpValue === 'object')
	{
		return null;
	}
	return { Field: pClause.Field, Operator: pClause.Operator, Value: tmpValue };
}

/**
 * @param {Array<FilterClause>} pClauses
 * @param {Object} pData - `{ User, Context }`
 * @return {{ Clauses: Array<FilterClause>, Unresolved: Array<FilterClause> }}
 */
function resolveClauses(pClauses, pData)
{
	const tmpResult = { Clauses: [], Unresolved: [] };
	for (const tmpClause of (Array.isArray(pClauses) ? pClauses : []))
	{
		const tmpResolved = resolveClause(tmpClause, pData);
		if (tmpResolved)
		{
			tmpResult.Clauses.push(tmpResolved);
		}
		else
		{
			tmpResult.Unresolved.push(tmpClause);
		}
	}
	return tmpResult;
}

/**
 * Compile resolved clauses into a meadow filter string (not URI-encoded).
 *
 * @param {Array<FilterClause>} pClauses
 * @return {string}
 */
function compileFilter(pClauses)
{
	return (Array.isArray(pClauses) ? pClauses : [])
		.filter((pClause) => OPERATORS[pClause.Operator])
		.map((pClause) =>
		{
			const tmpOperator = OPERATORS[pClause.Operator];
			if (tmpOperator.List)
			{
				return `FBL~${pClause.Field}~${tmpOperator.Meadow}~${[].concat(pClause.Value).join(',')}`;
			}
			return `FBV~${pClause.Field}~${tmpOperator.Meadow}~${pClause.Value}`;
		})
		.join('~');
}

/**
 * @param {FilterClause} pClause
 * @return {string}
 */
function clauseKey(pClause)
{
	return `${pClause.Field}|${pClause.Operator}|${JSON.stringify(pClause.Value)}`;
}

/**
 * Combine the filters of every rule that grants one dashboard. Most permissive wins: an unfiltered grant means no role
 * filter at all; otherwise only clauses common to every grant apply, so the result shows at least what each rule would.
 *
 * @param {Array<Array<FilterClause>|null>} pGrantFilters
 * @return {Array<FilterClause>}
 */
function combineGrantFilters(pGrantFilters)
{
	if (pGrantFilters.length < 1 || pGrantFilters.some((pClauses) => !Array.isArray(pClauses) || pClauses.length < 1))
	{
		return [];
	}
	const [ tmpFirst, ...tmpRest ] = pGrantFilters;
	return tmpFirst.filter((pClause) => tmpRest.every((pClauses) => pClauses.some((pOther) => clauseKey(pOther) === clauseKey(pClause))));
}

/**
 * @param {CatalogEntry} pEntry
 * @param {Array<string>} pGrantedBy
 * @param {Array<FilterClause>} pClauses
 * @param {Object} pData
 * @return {CatalogEntry & ResolvedDashboard}
 */
function buildResolved(pEntry, pGrantedBy, pClauses, pData)
{
	const tmpResolved = resolveClauses(pClauses, pData);
	return Object.assign({}, pEntry, { GrantedBy: pGrantedBy, DataFilter: tmpResolved.Clauses, DataFilterString: compileFilter(tmpResolved.Clauses), Unresolved: tmpResolved.Unresolved });
}

/**
 * Resolve which dashboards a user may open.
 *
 * @param {Object} pRequest
 * @param {Record<string, AccessRule>} pRequest.Rules - The merged `DashboardAccess` / `Rules` map.
 * @param {Array<CatalogEntry>} pRequest.Catalog - Every dashboard the customer has, in display order.
 * @param {Array<RoleRecord>} pRequest.CustomerRoles - The user's customer roles (with child roles).
 * @param {Array<RoleRecord>} [pRequest.ProjectRoles] - Their roles on Context.IDProject.
 * @param {{ IDProject?: number }} [pRequest.Context]
 * @param {Object} [pRequest.User] - Token values: IDUser, IDCustomer, LabIDs, ProjectIDs, CertifiedTestHashes.
 * @param {boolean} [pRequest.IsSuperUser]
 * @return {{ Enforced: boolean, Landing: string|null, Dashboards: Array<CatalogEntry & ResolvedDashboard>, Explain: Array<string> }}
 */
function resolveAccess(pRequest)
{
	const tmpCatalog = Array.isArray(pRequest.Catalog) ? pRequest.Catalog : [];
	const tmpContext = pRequest.Context || {};
	const tmpData = { User: pRequest.User || {}, Context: tmpContext };
	const tmpExplain = [];

	if (pRequest.IsSuperUser)
	{
		tmpExplain.push('super-user: every dashboard, no role filters');
		return { Enforced: false, Landing: null, Dashboards: tmpCatalog.map((pEntry) => buildResolved(pEntry, [ 'super-user' ], [], tmpData)), Explain: tmpExplain };
	}
	if (!hasRules(pRequest.Rules))
	{
		tmpExplain.push('no access rules for this customer: every dashboard, no role filters');
		return { Enforced: false, Landing: null, Dashboards: tmpCatalog.map((pEntry) => buildResolved(pEntry, [ 'no rules' ], [], tmpData)), Explain: tmpExplain };
	}

	const tmpCatalogByHash = new Map(tmpCatalog.map((pEntry) => [ pEntry.Hash, pEntry ]));
	/** @type {Map<string, Array<{ RuleKey: string, Clauses: Array<FilterClause>|null }>>} */
	const tmpGrants = new Map();
	let tmpLanding = null;
	let tmpFallbackLanding = null;

	for (const [ tmpKey, tmpRule ] of orderRules(pRequest.Rules))
	{
		const tmpCustomerMatch = matchRoles(tmpRule.Audience, pRequest.CustomerRoles);
		const tmpProjectMatch = tmpContext.IDProject ? matchRoles(tmpRule.Audience, pRequest.ProjectRoles) : { Match: false, Reason: 'no project' };
		if (!tmpCustomerMatch.Match && !tmpProjectMatch.Match)
		{
			tmpExplain.push(`${tmpKey}: skipped (${tmpCustomerMatch.Reason})`);
			continue;
		}
		const tmpGrantedHere = [];
		for (const tmpHash of tmpRule.Dashboards)
		{
			const tmpEntry = tmpCatalogByHash.get(tmpHash);
			if (!tmpEntry)
			{
				tmpExplain.push(`${tmpKey}: ${tmpHash} skipped (not a dashboard this customer has)`);
				continue;
			}
			if (!tmpCustomerMatch.Match && !tmpEntry.ProjectBound)
			{
				tmpExplain.push(`${tmpKey}: ${tmpHash} skipped (project role, dashboard is not project-bound)`);
				continue;
			}
			const tmpClauses = (tmpRule.DataFilters && Array.isArray(tmpRule.DataFilters[tmpHash])) ? tmpRule.DataFilters[tmpHash] : null;
			if (!tmpGrants.has(tmpHash))
			{
				tmpGrants.set(tmpHash, []);
			}
			tmpGrants.get(tmpHash).push({ RuleKey: tmpKey, Clauses: tmpClauses });
			tmpGrantedHere.push(tmpHash);
		}
		tmpExplain.push(`${tmpKey}: matched (${tmpCustomerMatch.Match ? 'customer role' : 'project role'}) → ${tmpGrantedHere.join(', ') || 'nothing'}`);
		// A project-bound dashboard can't open without a project, so it is never a landing outside one.
		const tmpOpenableHere = tmpGrantedHere.filter((pHash) => tmpContext.IDProject || !tmpCatalogByHash.get(pHash).ProjectBound);
		if (!tmpLanding && tmpRule.Landing && tmpOpenableHere.includes(tmpRule.Landing))
		{
			tmpLanding = tmpRule.Landing;
		}
		else if (!tmpFallbackLanding && tmpRule.Landing && !tmpOpenableHere.includes(tmpRule.Landing) && tmpOpenableHere.length > 0)
		{
			tmpFallbackLanding = tmpOpenableHere[0];
			tmpExplain.push(`${tmpKey}: landing ${tmpRule.Landing} not available here; falls back to ${tmpFallbackLanding}`);
		}
	}
	if (!tmpLanding)
	{
		tmpLanding = tmpFallbackLanding;
	}

	const tmpDashboards = tmpCatalog
		.filter((pEntry) => tmpGrants.has(pEntry.Hash))
		.map((pEntry) =>
		{
			const tmpEntryGrants = tmpGrants.get(pEntry.Hash);
			return buildResolved(pEntry, tmpEntryGrants.map((pGrant) => pGrant.RuleKey), combineGrantFilters(tmpEntryGrants.map((pGrant) => pGrant.Clauses)), tmpData);
		});
	return { Enforced: true, Landing: tmpLanding, Dashboards: tmpDashboards, Explain: tmpExplain };
}

/**
 * Plain-language sentence for a clause, e.g. "Lab is one of their labs".
 *
 * @param {FilterClause} pClause
 * @param {Record<string, string>} [pFieldLabels] - Field → label.
 * @return {string}
 */
function describeClause(pClause, pFieldLabels)
{
	const tmpField = (pFieldLabels && pFieldLabels[pClause.Field]) || pClause.Field;
	const tmpOperator = OPERATORS[pClause.Operator];
	const tmpVerb = tmpOperator ? tmpOperator.Label : pClause.Operator;
	let tmpValue = pClause.Value;
	if (typeof tmpValue === 'string')
	{
		const tmpWhole = WHOLE_TOKEN_PATTERN.exec(tmpValue);
		if (tmpWhole && TOKENS[tmpWhole[1]])
		{
			return `${tmpField} ${tmpVerb} ${TOKENS[tmpWhole[1]].Label}`;
		}
	}
	if (Array.isArray(tmpValue))
	{
		tmpValue = tmpValue.join(', ');
	}
	return `${tmpField} ${tmpVerb} ${tmpValue}`;
}

/**
 * What to tell the person viewing about filter clauses dropped for them (a dashboard's `Unresolved`): one notice per
 * missing value, in plain language. A clause with no `Missing` copy gets a general notice naming the filter.
 *
 * @param {Array<FilterClause>} pClauses
 * @param {Record<string, string>} [pFieldLabels] - Field → label.
 * @return {Array<{ Title: string, Message: string }>}
 */
function describeUnresolved(pClauses, pFieldLabels)
{
	const tmpNotices = [];
	const tmpSeen = new Set();
	for (const tmpClause of (Array.isArray(pClauses) ? pClauses : []))
	{
		const tmpWhole = (typeof tmpClause.Value === 'string') ? WHOLE_TOKEN_PATTERN.exec(tmpClause.Value) : null;
		const tmpToken = tmpWhole && TOKENS[tmpWhole[1]];
		const tmpNotice = (tmpToken && tmpToken.Missing) ? tmpToken.Missing :
			{
				Title: 'Showing more than usual',
				Message: `Part of this dashboard's usual filter (${describeClause(tmpClause, pFieldLabels)}) isn't set up for you in HeadLight, so it shows more than it normally would. If that looks wrong, contact HeadLight support.`,
			};
		if (!tmpSeen.has(tmpNotice.Title + tmpNotice.Message))
		{
			tmpSeen.add(tmpNotice.Title + tmpNotice.Message);
			tmpNotices.push({ Title: tmpNotice.Title, Message: tmpNotice.Message });
		}
	}
	return tmpNotices;
}

module.exports =
{
	NAMESPACE,
	NAMESPACE_VALUE,
	OPERATORS,
	TOKENS,
	orderRules,
	hasRules,
	matchRoles,
	resolveClause,
	resolveClauses,
	compileFilter,
	combineGrantFilters,
	resolveAccess,
	describeClause,
	describeUnresolved,
};
